
import React, { useEffect, useState } from "react";
import DataTable, { defaultThemes } from "react-data-table-component";
import { useHistory } from "react-router-dom";
import { Tooltip, Chip, CircularProgress, IconButton } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import List from "@material-ui/core/List";
import Toolbar from "@material-ui/core/Toolbar";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import { Button, Modal } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast } from "react-toastify";
import moment from "moment";
import EditIcon from "@material-ui/icons/Edit";
import EmailIcon from "@material-ui/icons/Email";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { ApiGet, ApiPost, ApiPut } from "../../../helpers/API/ApiData";
import Auth from "../../../helpers/Auth";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [description, setDescription] = useState("");
  const [errorDescriptions, setErrorDescriptions] = useState("");
  const [bookingToken, setBookingToken] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const getUserInfo = Auth.getUserData;

  const history = useHistory();

  useEffect(() => {
    fetchMeetings(1, pagination.limit, statusFilter, searchQuery);
  }, []);

  const fetchMeetings = async (page, limit, status, email) => {
    setLoading(true);
    try {
      const query = `?page=${page}&limit=${limit}${status !== "all" ? `&status=${status}` : ""}${email ? `&email=${email}` : ""}`;
      const res = await ApiGet(`plan/admin/getAdminMeetings${query}`);
      const { meetings, pagination: pag } = res.data.data;
      setMeetings(meetings);
      setFilteredMeetings(meetings);
      setPagination(pag);
    } catch (err) {
      console.error("Error fetching admin meetings:", err);
      toast.error(err.message || "Failed to load meetings");
      if (err.response?.status === 401 || err.response?.status === 403) {
        history.push("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = meetings.filter(
      (item) =>
        item.user.email.toLowerCase().includes(value) ||
        (item.meeting.title || "").toLowerCase().includes(value) ||
        (item.inviteeName || "").toLowerCase().includes(value)
    );
    setFilteredMeetings(filtered);
    if (!value) {
      fetchMeetings(pagination.page, pagination.limit, statusFilter, "");
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchMeetings(pagination.page, pagination.limit, status, searchQuery);
  };

  const handlePageChange = (page) => {
    fetchMeetings(page, pagination.limit, statusFilter, searchQuery);
  };

  const handleRowsPerPageChange = (newLimit) => {
    fetchMeetings(1, newLimit, statusFilter, searchQuery);
  };

  const handleEditNotes = (token, notes) => {
    setBookingToken(token);
    setDescription(notes || "");
    setOpenCreate(true);
  };

  const handleAddNotes = async () => {
    if (!description.trim()) {
      setErrorDescriptions("Please enter notes");
      return;
    }
    try {
      const res = await ApiPut(
        `/plan/my-meeting/update-notes`,
        {
          bookingToken,
          userId: getUserInfo()._id,
          notes: description,
        }
      );
      if (res.data.success) {
        toast.success("Notes updated successfully");
        setOpenCreate(false);
        setDescription("");
        setErrorDescriptions("");
        fetchMeetings(pagination.page, pagination.limit, statusFilter, searchQuery);
      } else {
        throw new Error(res.data.message || "Failed to update notes");
      }
    } catch (err) {
      console.error("Error updating notes:", err);
      toast.error(err.message || "Failed to update notes");
    }
  };

  const handleSendReminder = async (bookingToken) => {
    try {
      const res = await ApiPost(
        "/plan/my-meeting/send-reminder",
        { bookingToken, userId: getUserInfo()._id }
      );
      if (res.data.success) {
        toast.success("Reminder sent successfully");
      } else {
        throw new Error(res.data.message || "Failed to send reminder");
      }
    } catch (err) {
      console.error("Error sending reminder:", err);
      toast.error(err.message || "Failed to send reminder");
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      // Fetch all meetings with a high limit to get all data
      const query = `?page=1&limit=10000${statusFilter !== "all" ? `&status=${statusFilter}` : ""}${searchQuery ? `&email=${searchQuery}` : ""}`;
      const res = await ApiGet(`plan/admin/getAdminMeetings${query}`);
      const allMeetings = res.data.data.meetings || [];

      const headers = [
        "S.No.",
        "User Name",
        "User Email",
        "Meeting Title",
        "Duration",
        "Price",
        "Start Time",
        "Status",
        "Invitee Name",
        "Invitee Email",
        "Notes",
      ];

      const escapeCsvValue = (value) => {
        if (!value) return "N/A";
        // Escape quotes and wrap in quotes if the value contains commas or quotes
        const stringValue = String(value);
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const data = allMeetings.map((row, index) => [
        index + 1,
        escapeCsvValue(row.user.name),
        escapeCsvValue(row.user.email),
        escapeCsvValue(row.meeting.title || "N/A"),
        escapeCsvValue(`${row.meeting.duration || 0} min`),
        escapeCsvValue(row.meeting.price > 0 ? `$${row.meeting.price.toFixed(2)}` : "Free"),
        escapeCsvValue(row.startTime ? moment(row.startTime).format("MMM Do YYYY, h:mm a") : "Not scheduled"),
        escapeCsvValue(row.status),
        escapeCsvValue(row.inviteeName || "N/A"),
        escapeCsvValue(row.inviteeEmail || "N/A"),
        escapeCsvValue(row.notes || "N/A"),
      ]);

      const csvContent = [
        headers.join(","),
        ...data.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `admin_meetings_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV exported successfully");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      toast.error(err.message || "Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
    setErrorDescriptions("");
    setDescription("");
  };

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => index + 1 + (pagination.page - 1) * pagination.limit,
      width: "80px",
      style: { fontWeight: "600", color: "#475569" },
    },
    {
      name: "User",
      cell: (row) => (
        <div style={{ fontSize: "0.9rem" }}>
          <div style={{ fontWeight: "500", color: "#1e3a8a" }}>{row.user.name}</div>
          <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>{row.user.email}</div>
        </div>
      ),
      sortable: true,
      selector: (row) => row.user.name,
      width: "200px",
    },
    {
      name: "Meeting Title",
      selector: (row) => row.meeting.title,
      sortable: true,
      cell: (row) => (
        <div style={{ fontWeight: "500", color: "#1e3a8a" }}>
          {row.meeting.title || "N/A"}
        </div>
      ),
      width: "180px",
    },
    {
      name: "Duration",
      selector: (row) => row.meeting.duration,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div style={{ color: "#475569", fontSize: "0.95rem" }}>
          {row.meeting.duration || "N/A"} min
        </div>
      ),
    },
    {
      name: "Price",
      selector: (row) => row.meeting.price,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div style={{ fontWeight: "500", color: row.meeting.price > 0 ? "#10b981" : "#475569" }}>
          {row.meeting.price > 0 ? `$${row.meeting.price.toFixed(2)}` : "Free"}
        </div>
      ),
    },
    {
      name: "Start Time",
      selector: (row) => row.startTime,
      sortable: true,
      cell: (row) => (
        <div style={{ color: "#475569", fontSize: "0.9rem" }}>
          {row.startTime ? moment(row.startTime).format("MMM Do YYYY, h:mm a") : "Not scheduled"}
        </div>
      ),
      width: "180px",
    },
    {
      name: "Status",
      cell: (row) => {
        const statusConfig = {
          scheduled: { label: "Scheduled", color: "#10b981", bgColor: "#d1fae5" },
          paid: { label: "Paid", color: "#1e3a8a", bgColor: "#dbeafe" },
          free: { label: "Free", color: "#6b7280", bgColor: "#f3f4f6" },
          canceled: { label: "Canceled", color: "#ef4444", bgColor: "#fee2e2" },
          pending: { label: "Pending", color: "#f59e0b", bgColor: "#fef3c7" },
        };
        const config = statusConfig[row.status.toLowerCase()] || statusConfig.pending;
        return (
          <Chip
            label={config.label}
            style={{
              backgroundColor: config.bgColor,
              color: config.color,
              fontWeight: "500",
              borderRadius: "16px",
              fontSize: "0.85rem",
            }}
          />
        );
      },
      selector: (row) => row.status,
      sortable: true,
      width: "130px",
    },
    {
      name: "Invitee",
      cell: (row) => (
        <div style={{ fontSize: "0.9rem" }}>
          <div style={{ fontWeight: "500", color: "#1e3a8a" }}>
            {row.inviteeName || "N/A"}
          </div>
          <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>
            {row.inviteeEmail || "N/A"}
          </div>
        </div>
      ),
      width: "180px",
    },
    // {
    //   name: "Actions",
    //   cell: (row) => (
    //     <div className="d-flex justify-content-between align-items-center" style={{ gap: "8px" }}>
    //       <Tooltip title="View Meeting Link">
    //         <IconButton
    //           onClick={() => window.open(row.schedulingUrl || row.meetingLink, "_blank")}
    //           style={{ color: "#3b82f6", fontSize: "1.1rem" }}
    //         >
    //           <LinkIcon />
    //         </IconButton>
    //       </Tooltip>
    //       {row.status !== "canceled" && (
    //         <Tooltip title="Edit Notes">
    //           <IconButton
    //             onClick={() => handleEditNotes(row.bookingToken, row.notes)}
    //             style={{ color: "#10b981", fontSize: "1.1rem" }}
    //           >
    //             <EditIcon />
    //           </IconButton>
    //         </Tooltip>
    //       )}
    //       {row.status === "scheduled" && (
    //         <Tooltip title="Send Reminder">
    //           <IconButton
    //             onClick={() => handleSendReminder(row.bookingToken)}
    //             style={{ color: "#f59e0b", fontSize: "1.1rem" }}
    //           >
    //             <EmailIcon />
    //           </IconButton>
    //         </Tooltip>
    //       )}
    //     </div>
    //   ),
    //   width: "150px",
    //   right: true,
    // },
  ];

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        borderTopColor: defaultThemes.default.divider.default,
        backgroundColor: "#f8fafc",
      },
    },
    headCells: {
      style: {
        "&:not(:last-of-type)": {
          borderRightStyle: "solid",
          borderRightWidth: "1px",
          borderRightColor: defaultThemes.default.divider.default,
        },
        color: "#1e3a8a",
        fontWeight: "600",
        fontSize: "0.95rem",
      },
    },
    cells: {
      style: {
        "&:not(:last-of-type)": {
          borderRightStyle: "solid",
          borderRightWidth: "1px",
          borderRightColor: defaultThemes.default.divider.default,
        },
        padding: "12px 16px",
      },
    },
    rows: {
      style: {
        "&:hover": {
          backgroundColor: "#f1f5f9",
        },
        transition: "background-color 0.2s ease",
      },
    },
  };

  return (
    <div className="card p-1">
      <div className="p-2 mb-2">
        <div className="row mb-4">
          <div className="col d-flex justify-content-between align-items-center">
            <h2 className="pl-3 pt-2 mb-0" style={{ color: "#1e3a8a", fontWeight: "600" }}>
              Admin Meetings
            </h2>
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control form-control-lg form-control-solid mr-2"
                placeholder="Search by email, title, or invitee"
                value={searchQuery}
                onChange={handleSearch}
                style={{ maxWidth: "300px" }}
              />
              <select
                className="form-control form-control-lg mr-2"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                style={{ maxWidth: "150px" }}
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
                <option value="canceled">Canceled</option>
                <option value="pending">Pending</option>
              </select>
              <IconButton
                onClick={() => fetchMeetings(pagination.page, pagination.limit, statusFilter, searchQuery)}
                style={{ color: "#1e3a8a" }}
              >
                <SearchIcon />
              </IconButton>
              <IconButton
                onClick={handleExportCSV}
                disabled={exportLoading}
                style={{ color: exportLoading ? "#d1d5db" : "#1e3a8a" }}
              >
                {exportLoading ? <CircularProgress size={20} /> : <GetAppIcon />}
              </IconButton>
            </div>
          </div>
        </div>
        <DataTable
          noHeader
          columns={columns}
          data={filteredMeetings}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="d-flex justify-content-center p-4">
              <CircularProgress size={50} style={{ color: "#e11d48" }} />
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={pagination.total}
          paginationDefaultPage={pagination.page}
          paginationPerPage={pagination.limit}
          paginationRowsPerPageOptions={[5, 10, 25, 50]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
        />
      </div>

      <Dialog
        fullScreen
        open={openCreate}
        onClose={handleCloseCreate}
        TransitionComponent={Transition}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleCloseCreate}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <List>
          <div className="form ml-30">
            <div className="form-group row">
              <label className="col-xl-3 col-lg-3 col-form-label">Notes</label>
              <div className="col-lg-9 col-xl-6">
                <CKEditor
                  editor={ClassicEditor}
                  data={description}
                  onChange={(event, editor) => {
                    setDescription(editor.getData());
                  }}
                />
                {errorDescriptions && (
                  <span
                    style={{
                      color: "red",
                      top: "5px",
                      fontSize: "12px",
                    }}
                  >
                    {errorDescriptions}
                  </span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center">
              <button
                onClick={handleAddNotes}
                className="btn btn-primary mr-2"
                style={{ backgroundColor: "#e11d48", borderColor: "#e11d48" }}
              >
                <span>Update Notes</span>
              </button>
            </div>
          </div>
        </List>
      </Dialog>
    </div>
  );
};

export default AdminMeetings;
